import React, { useState, useCallback } from "react";

import Nouislider from "nouislider-react";

import { Controller } from "react-hook-form";

import "@styles/react/libs/noui-slider/noui-slider.scss";

import * as S from "./styles";

const RangeSliderWithForm = ({
  minValue,
  maxValue,
  step,
  control,
  name,
  defaultValue,
  renderValue,
  disabled,
  handleChange,
  tooltips,
  pips,
  direction,
  tooltipPlacement,
  color,
}) => {
  const memoizedHandleChange = useCallback(
    (onChange, values, handle) => {
      const newValue = parseFloat(values[handle]);
      onChange({ target: { name, value: newValue } });
      if (handleChange) {
        handleChange({ target: { name, value: newValue } });
      }
    },
    [name, handleChange],
  );

  return (
    <Controller
      control={control}
      name={name}
      defaultValue={defaultValue}
      render={({ field: { onChange, value } }) => {
        const currentValue = renderValue ? renderValue(value) : value;

        return (
          <S.NouiContainer placement={tooltipPlacement} color={color}>
            <Nouislider
              start={currentValue || defaultValue}
              disabled={disabled}
              direction={direction || "ltr"}
              connect={[true, false]}
              tooltips={tooltips}
              pips={pips}
              step={step}
              range={{
                min: minValue,
                max: maxValue,
              }}
              onChange={(render, handle, values) =>
                memoizedHandleChange(onChange, values, handle)
              }
            />
          </S.NouiContainer>
        );
      }}
    />
  );
};

const RangeSliderStandalone = ({
  minValue,
  maxValue,
  step,
  disabled,
  handleChange,
  value: controlledValue,
  defaultValue,
  tooltips,
  pips,
  direction,
  tooltipPlacement,
  color,
}) => {
  const [internalValue, setInternalValue] = useState(defaultValue || minValue);
  const value = controlledValue !== undefined ? controlledValue : internalValue;

  const handleSliderChange = useCallback(
    (render, handle, values) => {
      const newValue = parseFloat(values[handle]);
      if (controlledValue === undefined) {
        setInternalValue(newValue);
      }
      if (handleChange) {
        handleChange(newValue);
      }
    },
    [controlledValue, handleChange],
  );

  return (
    <S.NouiContainer placement={tooltipPlacement} color={color}>
      <Nouislider
        start={value}
        disabled={disabled}
        direction={direction || "ltr"}
        connect={[true, false]}
        tooltips={tooltips}
        pips={pips}
        step={step}
        range={{
          min: minValue,
          max: maxValue,
        }}
        onChange={handleSliderChange}
      />
    </S.NouiContainer>
  );
};

export const RangeSlider = ({
  minValue = 0,
  maxValue = 100,
  step = 1,
  control,
  name,
  defaultValue,
  renderValue,
  disabled = false,
  handleChange = () => {},
  value = 0,
  tooltips = true,
  pips,
  direction,
  tooltipPlacement = "top",
  color = "primary",
}) => {
  if (control) {
    return (
      <RangeSliderWithForm
        minValue={minValue}
        maxValue={maxValue}
        step={step}
        control={control}
        name={name}
        defaultValue={defaultValue}
        renderValue={renderValue}
        disabled={disabled}
        handleChange={handleChange}
        tooltips={tooltips}
        pips={pips}
        direction={direction}
        tooltipPlacement={tooltipPlacement}
        color={color}
      />
    );
  }

  return (
    <RangeSliderStandalone
      minValue={minValue}
      maxValue={maxValue}
      step={step}
      disabled={disabled}
      handleChange={handleChange}
      value={value}
      defaultValue={defaultValue}
      tooltips={tooltips}
      pips={pips}
      direction={direction}
      tooltipPlacement={tooltipPlacement}
      color={color}
    />
  );
};
